import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { 
  RefreshCw, 
  Clock, 
  Play, 
  Pause,
  Trash2, 
  Calendar,
  Moon,
  Sun,
  ListTodo,
  Settings,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Timer,
  Zap,
  CalendarClock,
  Newspaper,
  Globe
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Couleurs par source
const SOURCE_COLORS = {
  'radar': 'bg-green-500/20 border-green-500/40 text-green-400',
  'sonar': 'bg-blue-500/20 border-blue-500/40 text-blue-400',
  'cible': 'bg-purple-500/20 border-purple-500/40 text-purple-400',
};

const SOURCE_ICONS = {
  'radar': Newspaper,
  'sonar': Globe,
  'cible': Zap,
};

const STATUS_BADGES = {
  'scheduled': { color: 'bg-yellow-500/20 border-yellow-500/40 text-yellow-400', label: 'Planifié' },
  'pending': { color: 'bg-blue-500/20 border-blue-500/40 text-blue-400', label: 'En attente' },
  'publishing': { color: 'bg-sky-500/20 border-sky-500/40 text-sky-400', label: 'Publication...' },
  'published': { color: 'bg-green-500/20 border-green-500/40 text-green-400', label: 'Publié' },
  'error': { color: 'bg-red-500/20 border-red-500/40 text-red-400', label: 'Erreur' },
  'cancelled': { color: 'bg-gray-500/20 border-gray-500/40 text-gray-400', label: 'Annulé' },
};

const PublicationQueue = () => {
  const [loading, setLoading] = useState(false);
  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState({
    enabled: true,
    min_spacing_minutes: 60,
    night_pause_enabled: true,
    night_pause_start: 1,
    night_pause_end: 6,
    max_per_day: 24,
    weekend_mode: 'normal'
  });
  const [sites, setSites] = useState({});
  const [showSettings, setShowSettings] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');

  // Charger la file d'attente
  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (sourceFilter) params.source_type = sourceFilter;
      
      const [queueRes, statsRes, settingsRes, sitesRes] = await Promise.all([
        axios.get(`${API}/api/publication-queue`, { params }),
        axios.get(`${API}/api/publication-queue/stats`),
        axios.get(`${API}/api/publication-queue/settings`),
        axios.get(`${API}/api/wordpress-sites`)
      ]);
      
      setQueue(queueRes.data);
      setStats(statsRes.data);
      setSettings(settingsRes.data);
      
      // Créer un mapping site_id -> site pour affichage
      const siteMap = {};
      sitesRes.data.forEach(s => { siteMap[s.id] = s; });
      setSites(siteMap);
      
    } catch (error) {
      console.error('Erreur chargement file:', error);
      toast.error('Erreur lors du chargement de la file d\'attente');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sourceFilter]);

  useEffect(() => {
    fetchQueue();
    // Rafraîchir toutes les 30 secondes
    const interval = setInterval(fetchQueue, 30000);
    return () => clearInterval(interval);
  }, [fetchQueue]);

  // Mettre à jour les paramètres
  const updateSettings = async (updates) => {
    try {
      const res = await axios.put(`${API}/api/publication-queue/settings`, updates);
      setSettings(res.data);
      toast.success('Paramètres mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Annuler une entrée
  const cancelEntry = async (entryId) => {
    try {
      await axios.post(`${API}/api/publication-queue/${entryId}/cancel`);
      toast.success('Publication annulée');
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  // Forcer la publication
  const forcePublish = async (entryId) => {
    try {
      const res = await axios.post(`${API}/api/publication-queue/${entryId}/publish-now`);
      if (res.data.success) {
        toast.success('Publication effectuée');
      } else {
        toast.error(res.data.error || 'Échec');
      }
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  // Démarrer/Arrêter le service
  const toggleService = async () => {
    try {
      if (settings.enabled) {
        await axios.post(`${API}/api/publication-queue/stop`);
        toast.success('Service arrêté');
      } else {
        await axios.post(`${API}/api/publication-queue/start`);
        toast.success('Service démarré');
      }
      await updateSettings({ enabled: !settings.enabled });
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Formater la date
  const formatDate = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Temps restant avant publication
  const getTimeRemaining = (scheduledAt) => {
    if (!scheduledAt) return null;
    const scheduled = new Date(scheduledAt);
    const now = new Date();
    const diff = scheduled - now;
    
    if (diff <= 0) return 'Imminent';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) return `${hours}h ${minutes}min`;
    return `${minutes}min`;
  };

  return (
    <div className="space-y-6" data-testid="publication-queue-page">
      {/* Header avec stats */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-sky-400 flex items-center gap-2">
            <CalendarClock className="w-7 h-7" />
            File d&apos;attente de publication
          </h1>
          <p className="text-slate-400 mt-1">
            Planification intelligente pour Radar, Sonar et Cible
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSettings(!showSettings)}
            className="border-slate-600"
            data-testid="settings-toggle"
          >
            <Settings className="w-4 h-4 mr-2" />
            Paramètres
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchQueue}
            disabled={loading}
            className="border-slate-600"
            data-testid="refresh-queue"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">En attente</p>
                  <p className="text-2xl font-bold text-yellow-400">{stats.total_pending || 0}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-400/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Publiés aujourd&apos;hui</p>
                  <p className="text-2xl font-bold text-green-400">{stats.published_today || 0}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-400/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Erreurs</p>
                  <p className="text-2xl font-bold text-red-400">{stats.by_status?.error || 0}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-400/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Prochain</p>
                  <p className="text-lg font-bold text-sky-400">
                    {stats.next_scheduled ? formatDate(stats.next_scheduled.scheduled_at) : '-'}
                  </p>
                </div>
                <Timer className="w-8 h-8 text-sky-400/50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Panneau de paramètres */}
      {showSettings && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Settings className="w-5 h-5 text-sky-400" />
              Paramètres de la file d&apos;attente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Service ON/OFF */}
            <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg">
              <div className="flex items-center gap-3">
                {settings.enabled ? (
                  <Play className="w-5 h-5 text-green-400" />
                ) : (
                  <Pause className="w-5 h-5 text-red-400" />
                )}
                <div>
                  <p className="font-medium">Service de publication</p>
                  <p className="text-sm text-slate-400">
                    {settings.enabled ? 'Actif - les articles sont publiés automatiquement' : 'En pause - les articles s\'accumulent'}
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.enabled}
                onCheckedChange={toggleService}
                data-testid="service-toggle"
              />
            </div>

            {/* Espacement */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-slate-400">Espacement minimum (minutes)</label>
                <Input
                  type="number"
                  value={settings.min_spacing_minutes}
                  onChange={(e) => updateSettings({ min_spacing_minutes: parseInt(e.target.value) || 60 })}
                  className="bg-slate-900 border-slate-600"
                  min={5}
                  max={720}
                  data-testid="spacing-input"
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm text-slate-400">Maximum par jour</label>
                <Input
                  type="number"
                  value={settings.max_per_day}
                  onChange={(e) => updateSettings({ max_per_day: parseInt(e.target.value) || 24 })}
                  className="bg-slate-900 border-slate-600"
                  min={1}
                  max={100}
                  data-testid="max-per-day-input"
                />
              </div>
            </div>

            {/* Pause nocturne */}
            <div className="p-4 bg-slate-900/50 rounded-lg space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Moon className="w-5 h-5 text-indigo-400" />
                  <div>
                    <p className="font-medium">Pause nocturne</p>
                    <p className="text-sm text-slate-400">Pas de publication pendant la nuit</p>
                  </div>
                </div>
                <Switch
                  checked={settings.night_pause_enabled}
                  onCheckedChange={(v) => updateSettings({ night_pause_enabled: v })}
                  data-testid="night-pause-toggle"
                />
              </div>
              
              {settings.night_pause_enabled && (
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className="space-y-2">
                    <label className="text-sm text-slate-400">Début (heure)</label>
                    <Input
                      type="number"
                      value={settings.night_pause_start}
                      onChange={(e) => updateSettings({ night_pause_start: parseInt(e.target.value) || 1 })}
                      className="bg-slate-800 border-slate-600"
                      min={0}
                      max={23}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-slate-400">Fin (heure)</label>
                    <Input
                      type="number"
                      value={settings.night_pause_end}
                      onChange={(e) => updateSettings({ night_pause_end: parseInt(e.target.value) || 6 })}
                      className="bg-slate-800 border-slate-600"
                      min={0}
                      max={23}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Mode week-end */}
            <div className="p-4 bg-slate-900/50 rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <Calendar className="w-5 h-5 text-orange-400" />
                <p className="font-medium">Mode week-end</p>
              </div>
              <div className="flex gap-2">
                {[
                  { value: 'normal', label: 'Normal', icon: Sun },
                  { value: 'reduced', label: 'Réduit', icon: Moon },
                  { value: 'paused', label: 'Pause', icon: Pause }
                ].map(({ value, label, icon: Icon }) => (
                  <Button
                    key={value}
                    variant={settings.weekend_mode === value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => updateSettings({ weekend_mode: value })}
                    className={settings.weekend_mode === value ? 'bg-sky-600' : 'border-slate-600'}
                    data-testid={`weekend-mode-${value}`}
                  >
                    <Icon className="w-4 h-4 mr-1" />
                    {label}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filtres */}
      <div className="flex gap-4 items-center">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-400">Statut:</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-slate-800 border border-slate-600 rounded px-3 py-1 text-sm"
            data-testid="status-filter"
          >
            <option value="">Tous</option>
            <option value="scheduled">Planifiés</option>
            <option value="published">Publiés</option>
            <option value="error">Erreurs</option>
            <option value="cancelled">Annulés</option>
          </select>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-400">Source:</span>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="bg-slate-800 border border-slate-600 rounded px-3 py-1 text-sm"
            data-testid="source-filter"
          >
            <option value="">Toutes</option>
            <option value="radar">Radar</option>
            <option value="sonar">Sonar</option>
            <option value="cible">Cible</option>
          </select>
        </div>
      </div>

      {/* Liste des entrées */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ListTodo className="w-5 h-5 text-sky-400" />
            Articles en file d&apos;attente ({queue.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {queue.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucun article en file d&apos;attente</p>
              <p className="text-sm mt-2">
                Activez la &quot;Planification intelligente&quot; sur vos sources Radar/Sonar/Cible
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {queue.map((entry) => {
                const SourceIcon = SOURCE_ICONS[entry.source_type] || Newspaper;
                const statusInfo = STATUS_BADGES[entry.status] || STATUS_BADGES.pending;
                const site = sites[entry.site_id];
                const timeRemaining = entry.status === 'scheduled' ? getTimeRemaining(entry.scheduled_at) : null;
                
                return (
                  <div
                    key={entry.id}
                    className="p-4 bg-slate-900/50 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors"
                    data-testid={`queue-entry-${entry.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {/* Titre et badges */}
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <Badge className={SOURCE_COLORS[entry.source_type] || 'bg-slate-500/20'}>
                            <SourceIcon className="w-3 h-3 mr-1" />
                            {entry.source_type?.toUpperCase()}
                          </Badge>
                          <Badge className={statusInfo.color}>
                            {statusInfo.label}
                          </Badge>
                          {timeRemaining && entry.status === 'scheduled' && (
                            <Badge className="bg-sky-500/20 border-sky-500/40 text-sky-400">
                              <Timer className="w-3 h-3 mr-1" />
                              {timeRemaining}
                            </Badge>
                          )}
                        </div>
                        
                        {/* Titre de l'article */}
                        <h3 className="font-medium text-slate-200 truncate">
                          {entry.article_data?.title || 'Sans titre'}
                        </h3>
                        
                        {/* Détails */}
                        <div className="flex items-center gap-4 mt-2 text-sm text-slate-400">
                          <span className="flex items-center gap-1">
                            <Globe className="w-3 h-3" />
                            {site?.name || entry.site_id}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(entry.scheduled_at)}
                          </span>
                          {entry.published_at && (
                            <span className="flex items-center gap-1 text-green-400">
                              <CheckCircle className="w-3 h-3" />
                              Publié: {formatDate(entry.published_at)}
                            </span>
                          )}
                        </div>
                        
                        {/* Erreur */}
                        {entry.error && (
                          <div className="mt-2 p-2 bg-red-900/20 border border-red-800/50 rounded text-sm text-red-400">
                            <AlertTriangle className="w-4 h-4 inline mr-1" />
                            {entry.error}
                          </div>
                        )}
                      </div>
                      
                      {/* Actions */}
                      {(entry.status === 'scheduled' || entry.status === 'pending') && (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => forcePublish(entry.id)}
                            className="border-green-600 text-green-400 hover:bg-green-900/30"
                            title="Publier maintenant"
                            data-testid={`force-publish-${entry.id}`}
                          >
                            <Zap className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => cancelEntry(entry.id)}
                            className="border-red-600 text-red-400 hover:bg-red-900/30"
                            title="Annuler"
                            data-testid={`cancel-${entry.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Note explicative */}
      <Card className="bg-slate-800/30 border-slate-700/50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
            <div className="text-sm text-slate-400">
              <p className="font-medium text-slate-300 mb-1">Comment activer la planification intelligente ?</p>
              <p>
                Dans les pages Radar, Sonar ou Cible, éditez une source et activez l&apos;option 
                <strong className="text-sky-400"> &quot;Planification intelligente&quot;</strong>. 
                Les articles seront alors ajoutés à cette file d&apos;attente au lieu d&apos;être publiés immédiatement.
              </p>
              <p className="mt-2">
                <strong>Mode actuel:</strong> La publication immédiate reste disponible si vous n&apos;activez pas cette option.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PublicationQueue;
